import React, { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";

import EventService from "service/event/EventService";
import { RAIL_CLOSE, RAIL_OPEN } from "../railEvents";
import PatientDetailsForm from "./PatientDetailsForm";
import { PD_V2_CLOSE, PD_V2_EDIT } from "./events";
import { overlayCapturedOnPatient } from "../aiTranscript/patientDetailsBodyAdapters";

/**
 * Patient details editor rail — the right column when a table row
 * click has opened the form.
 *
 * Same shell contract every editorOnDemand section follows:
 *   • listen for the section's OPEN event (PD_V2_EDIT) and stash the
 *     opener's payload (`focusField`) in local state
 *   • toggle a boolean; emit RAIL_OPEN / RAIL_CLOSE so PatientShell
 *     shows / hides the 340px column at the right times
 *   • render the form once open; unmount it on close so the form's
 *     internal state is fresh next time
 *   • surface a PD_V2_CLOSE broadcast so a saver (or the header
 *     dialog) can programmatically shut this rail down without
 *     touching internal state
 *
 * Only one editing session exists at any moment — clicking a second
 * row while the rail is open just replaces the focus target,
 * matching the dialog's single-instance model.
 */
const PatientDetailsEditor = ({
  patient,
  onManualSave,
  capturedItems,
  open: openProp,
  focusField: focusFieldProp,
  focusAt: focusAtProp,
  onClose: onCloseProp,
  themeClass,
}) => {
  // Controlled vs uncontrolled. Standalone mounts leave the *Prop
  // pairs undefined and the internal event-bus listener drives state.
  // Transcript mode passes both open and onClose so CapturedSections
  // owns the state directly — the event bus is bypassed for that
  // path since the direct callback is more predictable across the
  // modal / shell double-mount scenario.
  const isControlled = typeof openProp === "boolean";

  const [openInternal, setOpenInternal] = useState(false);
  const [focusFieldInternal, setFocusFieldInternal] = useState(null);
  const [focusAtInternal, setFocusAtInternal] = useState(0);

  const open = isControlled ? openProp : openInternal;
  const focusField = isControlled ? focusFieldProp || null : focusFieldInternal;
  const focusAt = isControlled ? focusAtProp || 0 : focusAtInternal;

  // Transcript mode passes captured items so the form seeds from the
  // STAGED overlay, not the raw record. Only one captured update ever
  // exists for patient_details (single-row section — see the body
  // adapter's uniqueKey convention).
  const stagedCaptured = useMemo(() => {
    if (!Array.isArray(capturedItems)) return null;
    return capturedItems.find((it) => (it && it.state) !== "reviewed") || null;
  }, [capturedItems]);
  const effectivePatient = useMemo(() => {
    if (!stagedCaptured || !stagedCaptured.payload) return patient;
    return overlayCapturedOnPatient(patient, stagedCaptured.payload);
  }, [patient, stagedCaptured]);

  const close = useCallback(() => {
    if (typeof onCloseProp === "function") {
      onCloseProp();
    } else {
      setOpenInternal(false);
      setFocusFieldInternal(null);
    }
    EventService.emit(RAIL_CLOSE);
  }, [onCloseProp]);

  useEffect(() => {
    // Skip the event bus entirely in controlled mode — the parent
    // owns state and any stray PD_V2_EDIT emit would just re-open a
    // rail we're already managing.
    if (isControlled) return undefined;
    const onEdit = (payload) => {
      setFocusFieldInternal((payload && payload.focusField) || null);
      setFocusAtInternal((payload && payload.focusAt) || 0);
      setOpenInternal(true);
      EventService.emit(RAIL_OPEN);
    };
    EventService.addListener(PD_V2_EDIT, onEdit);
    EventService.addListener(PD_V2_CLOSE, close);
    return () => {
      EventService.removeListener(PD_V2_EDIT, onEdit);
      EventService.removeListener(PD_V2_CLOSE, close);
      // Ensure the shell doesn't leave the rail pinned open when
      // the reviewer navigates away mid-edit.
      EventService.emit(RAIL_CLOSE);
    };
  }, [close, isControlled]);

  if (!open) return null;

  return (
    <PatientDetailsForm
      patient={effectivePatient}
      focusField={focusField}
      focusAt={focusAt}
      onClose={close}
      onSaved={close}
      onManualSave={onManualSave}
      themeClass={themeClass}
    />
  );
};

PatientDetailsEditor.propTypes = {
  patient: PropTypes.object.isRequired,
  /** Transcript mode: routes form save through /captured instead of bridge. */
  onManualSave: PropTypes.func,
  /** Transcript mode: staged captured items for the patient_details section. */
  capturedItems: PropTypes.arrayOf(PropTypes.object),
  /** Controlled-mode open flag. When boolean, the event bus is
   *  bypassed and the parent owns state. Standalone leaves undefined. */
  open: PropTypes.bool,
  /** Controlled-mode focus target. See PatientDetailsForm.focusField. */
  focusField: PropTypes.string,
  /** Controlled-mode click token — changes on every row click, so asking for
      the SAME field twice still moves the focus. */
  focusAt: PropTypes.number,
  /** Controlled-mode close handler. Called on Cancel + successful Save. */
  onClose: PropTypes.func,
  /** Passed through to the form root — "is-ai" for maroon theming. */
  themeClass: PropTypes.string,
};

PatientDetailsEditor.defaultProps = {
  onManualSave: null,
  capturedItems: null,
  open: undefined,
  focusField: null,
  focusAt: 0,
  onClose: null,
  themeClass: "",
};

export default PatientDetailsEditor;
